'use client';
import React, { useEffect, useMemo, useState } from 'react';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import { AiFillSignal } from 'react-icons/ai';
import Icon from '@/components/atoms/icon';
import useGetTags from '@/helpers/useGetTags';
import Skeleton from '@/components/atoms/skeletons';
import { ContextualIndicator } from '@/interfaces/home';
import dynamic from 'next/dynamic';
import { useGetTagChartData } from '@/helpers/useGetTagChartData';
import Empty from '@/components/atoms/empty';
import { SCREEN } from '@/statics/constants';
import { mapModifiers, slugify } from '@/utils/funtion';
import { useDispatch, useSelector } from 'react-redux';
import ChartPackageSkeleton from '@/components/molecules/skeleton-component/chartPacakge';
import { FaArrowAltCircleRight } from 'react-icons/fa';
import Input from '@/components/atoms/Input';
import { IoSearch } from 'react-icons/io5';
import dayjs from 'dayjs';
import { setDetailUrl } from '@/stores/modules/detailUrl';

const ChartPackage = dynamic(
  () => import('@/components/templates/chartPackage'),
  {
    ssr: false,
    loading: () => {
      return <ChartPackageSkeleton />;
    },
  },
);

const MapsPackage = dynamic(
  () => import('@/components/templates/mapsPackage'),
  {
    ssr: false,
    loading: () => {
      return <ChartPackageSkeleton />;
    },
  },
);

export default function HomeMacroeconomics({
  homepage,
}: {
  homepage: boolean;
}) {
  const { tags, isLoading } = useGetTags(
    'chi-bao-kinh-te-vi-mo-chinh',
    SCREEN.HOME,
  );
  const { chartData, loading, handleGetChardData, handleGetChartDataByRange } =
    useGetTagChartData();
  const [activeItem, setActiveItem] = useState(
    tags?.contextualIndicators && tags?.contextualIndicators?.length > 0
      ? tags?.contextualIndicators[0].id
      : '',
  );
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined,
  );
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.userData);
  const [searchData, setSearchData] = useState('');
  const [isSortedReady, setIsSortedReady] = useState(false);
  const [type, setType] = useState('');
  const [initialData, setInitialData] = useState({
    key: '',
    value: undefined as any,
  });
  const area = 'VN';
  const sortedIndicators = useMemo(() => {
    if (!tags?.contextualIndicators) return [];
    return [...tags.contextualIndicators].sort((a, b) => {
      const aUpdatedAt = new Date(
        a?.indicator?.updatedAt ||
          a?.industryIndicator?.updatedAt ||
          a?.localIndicator?.updatedAt,
      );
      const bUpdatedAt = new Date(
        b?.indicator?.updatedAt ||
          b?.industryIndicator?.updatedAt ||
          b?.localIndicator?.updatedAt,
      );
      const isAToday = aUpdatedAt.toDateString() === new Date().toDateString();
      const isBToday = bUpdatedAt.toDateString() === new Date().toDateString();
      if (isAToday && !isBToday) return -1;
      if (!isAToday && isBToday) return 1;
      return bUpdatedAt.getTime() - aUpdatedAt.getTime();
    });
  }, [tags?.contextualIndicators]);

  useEffect(() => {
    if (sortedIndicators && sortedIndicators.length > 0) {
      setIsSortedReady(true);
    }
  }, [sortedIndicators]);

  useEffect(() => {
    if (selectedTime) {
      handleGetChardData(
        activeItem,
        'local_indicator',
        chartData?.isStructure ? 1 : 0,
        selectedTime,
        chartData?.isObjectOneTime ? 1 : 0,
      );
    }
  }, [selectedTime]);

  useEffect(() => {
    const values = chartData?.values || {};
    for (const key in values) {
      if (Object.keys(values[key]).length > 0) {
        setInitialData({
          key,
          value: values[key],
        });
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (isSortedReady && sortedIndicators?.length > 0 && !activeItem) {
      const firstIndicator = sortedIndicators[0];
      const { indicator, industryIndicator, localIndicator } = firstIndicator;
      const selectedIndicator =
        indicator || industryIndicator || localIndicator;

      if (selectedIndicator) {
        setActiveItem(selectedIndicator.id);
        handleGetChardData(
          selectedIndicator.id,
          selectedIndicator.type,
          selectedIndicator.isStructure ? 1 : 0,
          '',
          selectedIndicator.isObjectOneTime ? 1 : 0,
        );
      }
    }
  }, [isSortedReady, activeItem, tags?.contextualIndicators]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchData(e.target.value);
  };

  const filteredIndicators = useMemo(() => {
    if (!searchData.trim()) return sortedIndicators || [];
    const exactMatch = searchData.match(/^["'](.*?)["']$/);
    return (
      sortedIndicators?.filter((item: ContextualIndicator) => {
        const name =
          item?.indicator?.name ||
          item?.localIndicator?.name ||
          item?.industryIndicator?.name;
        if (!name) return false;
        if (exactMatch) {
          return name.toLowerCase().includes(exactMatch[1].toLowerCase());
        } else {
          const searchWords = searchData.toLowerCase().split(/\s+/);
          return searchWords.every((word) => name.toLowerCase().includes(word));
        }
      }) || []
    );
  }, [searchData, sortedIndicators]);

  const handleSelectTime = (value: string) => {
    setSelectedTime(value);
  };

  const mappingData: Record<string, any> = {
    indicator: chartData?.values?.indicator,
    industryIndicator: chartData?.values?.industryIndicator,
    localIndicator: chartData?.values?.localIndicator,
  };

  const chartMemories = useMemo(() => {
    if (!chartData) return null;

    if (
      (initialData.key === 'localIndicator' &&
        !!chartData?.values?.localIndicator?.map?.length) ||
      !!initialData?.value?.map?.length
    ) {
      return (
        <MapsPackage
          area={area}
          id={chartData?.id}
          dataMaps={chartData?.values?.localIndicator?.map || []}
          original={chartData?.original}
          originalUrl={chartData?.originalUrl}
          loading={chartData?.loading}
          unit={chartData?.unit}
          mapTitle={chartData?.name}
          timeData={chartData?.yearList}
          chartType={chartData?.chartType}
          onSelectTime={handleSelectTime}
        />
      );
    }

    const {
      isStructure,
      isObjectOneTime,
      isTwoUnit,
      name,
      id,
      original,
      originalUrl,
      unit,
      dataType,
      chartType,
      parentId,
      values,
    } = chartData;

    const typeData =
      Object.keys(values?.indicator || {}).length > 0
        ? 'indicator'
        : Object.keys(values?.industryIndicator || {}).length > 0
          ? 'industry_indicator'
          : 'local_indicator';

    const result =
      [
        values?.indicator,
        values?.industryIndicator,
        values?.localIndicator,
      ].find(
        (item) =>
          item?.children?.length ||
          item?.often?.length ||
          item?.children?.response?.length ||
          item?.map?.length,
      ) || null;

    let data = null;
    if (!isStructure && !isObjectOneTime && !isTwoUnit) {
      data = result?.children?.length ? result?.children : result?.often;
    } else if (isStructure) {
      data = result?.children?.response || result?.map;
    } else {
      data = result?.children?.length
        ? result?.children
        : result?.often?.length
          ? result?.often
          : result?.chart;
    }

    const handleClickRangeTime = (item: any) => {
      handleGetChartDataByRange(item, typeData, isStructure ? 1 : 0);
    };

    return (
      <div className="t-home-economics_right">
        <ChartPackage
          data={data}
          isHomePage={homepage}
          isStructure={isStructure}
          name={name}
          slug={slugify(name)}
          id={id}
          loading={loading}
          original={original}
          originalUrl={originalUrl}
          unit={unit}
          isTwoUnit={isTwoUnit}
          dataType={dataType}
          isObjectOneTime={isObjectOneTime}
          chartType={chartType || 'column'}
          handleClickRangeTime={handleClickRangeTime}
          typeData={typeData}
          parentId={parentId}
        />
      </div>
    );
  }, [chartData, loading, mappingData, initialData, area, type]);

  return (
    <>
      <div className="t-home-economics">
        <div className="t-home-economics_left">
          <div className="t-home-economics_search">
            <Input
              type="search"
              placeholder="Tìm kiếm chỉ số"
              value={searchData}
              onChange={handleInputChange}
              enterButton={<IoSearch />}
            />
          </div>
          <div className="macroeconomics-list">
            {isLoading ? (
              <Skeleton
                type="node"
                style={{ width: '100%', minHeight: 400 }}
                active
              />
            ) : filteredIndicators && filteredIndicators?.length > 0 ? (
              filteredIndicators?.map((item: ContextualIndicator) => {
                const isToday = dayjs(
                  (
                    item.indicator ||
                    item.industryIndicator ||
                    item.localIndicator
                  ).updatedAt,
                ).isAfter(dayjs().subtract(3, 'day'));
                return (
                  <div
                    key={item?.id}
                    className={mapModifiers(
                      'macroeconomics-list_item',
                      activeItem ===
                        (item?.indicator?.id ||
                          item?.industryIndicator?.id ||
                          item?.localIndicator?.id)
                        ? 'active'
                        : '',
                    )}
                    onClick={() => {
                      const { indicator, industryIndicator, localIndicator } =
                        item;
                      const selectedIndicator =
                        indicator || industryIndicator || localIndicator;

                      if (selectedIndicator) {
                        setActiveItem(selectedIndicator.id);
                        handleGetChardData(
                          selectedIndicator.id,
                          selectedIndicator.type,
                          selectedIndicator.isStructure ? 1 : 0,
                          '',
                          selectedIndicator.isObjectOneTime ? 1 : 0,
                        );
                      }
                      setType(selectedIndicator?.type);
                    }}
                  >
                    <div className="macroeconomics-list_item_icon">
                      <Icon
                        component={
                          activeItem ===
                          (item?.indicator?.id ||
                            item?.industryIndicator?.id ||
                            item?.localIndicator?.id) ? (
                            <FaArrowAltCircleRight
                              style={{ color: '#05d335' }}
                            />
                          ) : (
                            <AiFillSignal />
                          )
                        }
                      />
                    </div>
                    <p>
                      {item?.indicator?.name ||
                        item?.localIndicator?.name ||
                        item?.industryIndicator?.name}
                      <a
                        href={`/thong-tin-chi-tiet/${item?.indicator?.slug || item?.industryIndicator?.slug || item?.localIndicator?.slug}/${item?.indicator?.id || item?.industryIndicator?.id || item?.localIndicator?.id}?type=${item?.indicator?.type || item?.industryIndicator?.type || item?.localIndicator?.type}&structure=${item?.indicator?.isStructure || item?.industryIndicator?.isStructure || item?.localIndicator?.isStructure}&isObjectOneTime=${item?.indicator?.isObjectOneTime || item?.industryIndicator?.isObjectOneTime || item?.localIndicator?.isObjectOneTime}`}
                      >
                        {' '}
                        [Chi tiết]
                      </a>
                    </p>
                    {isToday && (
                      <div className="macroeconomics-list_item_new">
                        <Icon iconName="newest" size="28x28" />
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <Empty />
            )}
          </div>
        </div>
        {chartMemories}
      </div>
    </>
  );
}
